/**
 * The creator's view.
 *
 * A creator opens this to answer one question — when do I get paid — so that is
 * what sits at the top, and it always has a real answer. "Payout unavailable"
 * with no reason is the failure mode this panel exists to avoid: every blocked
 * state below says what is holding it and what changes it.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useCreatorActivity,
  useCreatorConnections,
  useEarnings,
  useForgetTaxInformation,
  useLinks,
  useMintReferralCode,
  usePayoutDestinations,
  usePayoutEligibility,
  usePayouts,
  useReferralCodes,
  useRequestPayout,
  useSubmitTaxInformation,
  useTaxInformation,
} from '../api/hooks.js';
import { Money, Stat } from './Money.js';
import { ActivityTable, ErrorNote, Panel } from './shared.js';
import { DestinationsPanel } from './DestinationsPanel.js';
import { StatementsPanel } from './StatementsPanel.js';
import type { PayoutEligibilityWire } from '../api/client.js';

export function CreatorDashboard() {
  const earnings = useEarnings(true);

  return (
    <div className="stack">
      <Panel title="Your earnings" subtitle="A share of every commission your fans generate.">
        {earnings.isError && <ErrorNote error={earnings.error} />}
        {/* Same treatment as the fan's balance, and for the same reason: a
            creator opens this to find out what they can take out. Deliberately
            not applied to the stats inside the payout panel below — a second
            display-size figure on one screen makes neither of them the lead. */}
        <div className="stats stats--lead">
          <Stat label="Ready to withdraw" value={earnings.data?.available} tone="positive" />
          <Stat
            label="Still maturing"
            value={earnings.data?.pending}
            hint="Until the shop's return window closes."
          />
          <Stat label="Being paid out" value={earnings.data?.inFlight} tone="muted" />
          <Stat label="Earned all time" value={earnings.data?.lifetimeEarned} tone="muted" />
        </div>
      </Panel>

      <PayoutPanel />
      {/* Directly under it: a withdrawal with nowhere to arrive is the one
          thing that blocks getting paid, so the fix sits next to the problem. */}
      <DestinationsPanel />
      {/* After the money, before the paperwork: what left, in a form that does
          not change under the reader. The activity feed at the foot of this
          page is the live version of the same facts and is not a record. */}
      <StatementsPanel kind="creator" enabled />
      <TaxPanel />
      <CodesPanel />
      <LinksPanel />
      <FansPanel />

      <Panel title="Your activity" subtitle="Every movement, exactly as recorded.">
        <CreatorActivity />
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Why a payout cannot happen yet, in the creator's terms.
 *
 * Each of these is a sentence someone can act on. `funds_on_hold` and
 * `below_minimum` in particular are different situations that a single "not
 * available" would flatten — one resolves by waiting, the other by earning more.
 */
function blockedExplanation(eligibility: PayoutEligibilityWire): string {
  switch (eligibility.blockedReason) {
    case 'tax_information_required':
      return 'Add your tax information below and this unlocks. Your balance keeps accruing either way.';
    case 'negative_balance':
      return 'A shop reversed a purchase that had already been paid out. It is recovered from future earnings — nothing will ever be debited from you.';
    case 'payout_in_flight':
      return 'A withdrawal is already on its way. The next one can start once it lands.';
    case 'funds_on_hold':
      return `Your earnings are still inside the ${eligibility.policy.holdDays}-day hold, which covers the window a shop can still refund a purchase.`;
    case 'below_minimum':
      return 'Not quite enough yet — withdrawals start from the minimum below.';
    default:
      return '';
  }
}

function PayoutPanel() {
  const eligibility = usePayoutEligibility(true);
  const payouts = usePayouts(true);
  const destinations = usePayoutDestinations(true);
  const request = useRequestPayout();

  /**
   * One key per attempt, held in state and replaced only once an attempt
   * succeeds — the same rule the fan's cash-out panel follows.
   *
   * This was `crypto.randomUUID()` inside the click handler, which produces a
   * fresh key for every click and so cannot identify two clicks as the same
   * intent. `disabled` while pending hides the double-click case but not the
   * one that matters: a creator who sees nothing happen because the response
   * was lost, and presses Withdraw again. That is the exact moment an
   * idempotency key is supposed to earn its keep, and a key minted at click
   * time is guaranteed to be useless for it.
   */
  const [attempt, setAttempt] = useState(() => crypto.randomUUID());

  const rows = useMemo(() => destinations.data ?? [], [destinations.data]);
  const [destinationId, setDestinationId] = useState<string | null>(null);

  /**
   * Follow the list rather than owning the choice. Without this, a destination
   * removed while this page is open stays selected and the withdrawal is
   * submitted against an id the server answers 404 for — an error about a row
   * that is no longer on screen.
   */
  useEffect(() => {
    if (rows.length === 0) {
      if (destinationId !== null) setDestinationId(null);
      return;
    }
    if (destinationId === null || !rows.some((row) => row.id === destinationId)) {
      setDestinationId(rows[0]!.id);
    }
  }, [rows, destinationId]);

  const data = eligibility.data;
  const blocked = data?.blockedReason ?? null;
  /**
   * A destination is now part of being able to withdraw, alongside the balance
   * rules. It is not a `blockedReason` because the server does not know one is
   * missing until the request arrives — and a creator finding that out from a
   * 404 after pressing Withdraw is the wrong order to learn it in.
   */
  const canWithdraw = data !== undefined && blocked === null && destinationId !== null;

  return (
    <Panel
      title="Get paid"
      subtitle="Withdraw everything that has cleared its hold."
      action={
        canWithdraw ? (
          <button
            className="button"
            onClick={() =>
              request.mutate(
                { idempotencyKey: attempt, destinationId: destinationId! },
                { onSuccess: () => setAttempt(crypto.randomUUID()) },
              )
            }
            disabled={request.isPending}
          >
            {request.isPending ? 'Sending…' : 'Withdraw'}
          </button>
        ) : undefined
      }
    >
      {eligibility.isError && <ErrorNote error={eligibility.error} />}
      {request.isError && <ErrorNote error={request.error} />}

      {data !== undefined && (
        <>
          <div className="stats">
            <Stat label="Available to withdraw" value={data.payable} tone="positive" />
            <Stat
              label="Still on hold"
              value={data.onHold}
              hint={`Clears ${data.policy.holdDays} days after each sale.`}
            />
            <Stat label="Minimum withdrawal" value={data.policy.minimumPayout} tone="muted" />
          </div>

          {blocked !== null && (
            <div className="pending-note">
              <strong>Not available yet</strong>
              <span>{blockedExplanation(data)}</span>
            </div>
          )}

          {blocked === null && rows.length === 0 && (
            /* Said as a next step, not a refusal. The money has cleared and
               nothing is wrong — there is simply nowhere to send it yet. */
            <div className="pending-note">
              <strong>Add somewhere to send it</strong>
              <span>Link a bank account below and Withdraw appears here.</span>
            </div>
          )}

          {blocked === null && rows.length > 1 && (
            <label className="field">
              <span className="field__label">Send it to</span>
              <select
                className="input"
                value={destinationId ?? ''}
                onChange={(event) => setDestinationId(event.target.value)}
              >
                {rows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      <PayoutHistory rows={payouts.data} loading={payouts.isPending} />
    </Panel>
  );
}

function PayoutHistory({
  rows,
  loading,
}: {
  rows: { id: string; amount: { amountCents: number; currency: 'USD' }; state: string; processorFee: { amountCents: number; currency: 'USD' }; failureReason: string | null; requestedAt: string }[] | undefined;
  loading: boolean;
}) {
  if (loading) return <p className="empty">Loading…</p>;
  if (rows === undefined || rows.length === 0) {
    return <p className="empty">No withdrawals yet.</p>;
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Requested</th>
            <th>Status</th>
            <th className="table__right">Fee</th>
            <th className="table__right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((payout) => (
            <tr key={payout.id}>
              <td className="table__muted">
                {new Date(payout.requestedAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </td>
              <td>
                <span className={`pill pill--${payout.state}`}>
                  {payout.state === 'requested' ? 'on its way' : payout.state}
                </span>
                {payout.failureReason !== null && (
                  <span className="table__note">{payout.failureReason}</span>
                )}
              </td>
              <td className="table__right table__muted">
                <Money value={payout.processorFee} />
              </td>
              <td className="table__right">
                <Money value={payout.amount} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Tax information.
 *
 * Only required above the reporting threshold, and the panel says so — a
 * creator who has earned $40 should not be asked for a Social Security number
 * and left wondering why.
 *
 * The value is write-only. Once submitted, only the last four digits ever come
 * back, and this panel cannot show more because the API will not return more.
 */
function TaxPanel() {
  const summary = useTaxInformation(true);
  const submit = useSubmitTaxInformation();
  const forget = useForgetTaxInformation();

  const [kind, setKind] = useState<'ssn' | 'ein' | 'itin'>('ssn');
  const [value, setValue] = useState('');

  const onFile = summary.data ?? null;

  return (
    <Panel
      title="Tax information"
      subtitle="Required once your earnings pass the reporting threshold."
    >
      {summary.isError && <ErrorNote error={summary.error} />}
      {submit.isError && <ErrorNote error={submit.error} />}
      {forget.isError && <ErrorNote error={forget.error} />}

      {onFile !== null ? (
        <div className="on-file">
          <div>
            <span className="on-file__label">{onFile.kind.toUpperCase()} on file</span>
            <span className="on-file__value">•••–••–{onFile.lastFour}</span>
          </div>
          <button
            className="button button--quiet"
            onClick={() => forget.mutate()}
            disabled={forget.isPending}
          >
            Remove
          </button>
        </div>
      ) : (
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = value.trim();
            if (trimmed.length > 0) {
              submit.mutate({ kind, value: trimmed }, { onSuccess: () => setValue('') });
            }
          }}
        >
          <label className="field">
            <span className="field__label">Type</span>
            <select
              className="input"
              value={kind}
              onChange={(event) => setKind(event.target.value as 'ssn' | 'ein' | 'itin')}
            >
              <option value="ssn">Social Security number</option>
              <option value="ein">Employer ID (EIN)</option>
              <option value="itin">ITIN</option>
            </select>
          </label>
          <label className="field field--grow">
            <span className="field__label">Number</span>
            <input
              className="input"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="123-45-6789"
              autoComplete="off"
              inputMode="numeric"
            />
          </label>
          <button className="button" type="submit" disabled={submit.isPending}>
            {submit.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}

      <p className="note">
        Stored encrypted under a key used for nothing else. We never show it
        again, and no part of this app can read it back.
      </p>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

function CodesPanel() {
  const codes = useReferralCodes(true);
  const mint = useMintReferralCode();
  const [code, setCode] = useState('');
  const [memberShare, setMemberShare] = useState(48);

  const rows = codes.data ?? [];
  // The platform's cut is fixed; a creator chooses how much of the rest to give
  // away, and seeing their own share move as they drag it is the point.
  const creatorShare = 80 - memberShare;

  return (
    <Panel
      title="Your codes"
      subtitle="Fans enter one of these to connect to you."
    >
      {mint.isError && <ErrorNote error={mint.error} />}

      <form
        className="split-form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = code.trim();
          if (trimmed.length === 0) return;
          mint.mutate(
            {
              code: trimmed,
              terms: {
                platformBps: 2000,
                creatorBps: creatorShare * 100,
                memberBps: memberShare * 100,
              },
            },
            { onSuccess: () => setCode('') },
          );
        }}
      >
        <label className="field">
          <span className="field__label">New code</span>
          <input
            className="input"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="e.g. yourname"
            autoComplete="off"
          />
        </label>

        <label className="field field--grow">
          <span className="field__label">
            Your fans keep {memberShare}% · you keep {creatorShare}%
          </span>
          <input
            type="range"
            min={10}
            max={70}
            step={1}
            value={memberShare}
            onChange={(event) => setMemberShare(Number(event.target.value))}
          />
          <span className="field__hint">
            Give more away and you are the reason they signed up.
          </span>
        </label>

        <button className="button" type="submit" disabled={mint.isPending}>
          {mint.isPending ? 'Creating…' : 'Create code'}
        </button>
      </form>

      {rows.length === 0 ? (
        <p className="empty">No codes yet.</p>
      ) : (
        <ul className="codes">
          {rows.map((row) => (
            <li key={row.id} className="code-row">
              <span className="code-row__code">{row.code}</span>
              <span className="code-row__terms">
                fan {Math.round(row.terms.memberBps / 100)}% · you{' '}
                {Math.round(row.terms.creatorBps / 100)}%
              </span>
              <span className={`pill pill--${row.active ? 'active' : 'inactive'}`}>
                {row.active ? 'active' : 'inactive'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

function LinksPanel() {
  const links = useLinks(true);
  const rows = links.data ?? [];

  return (
    <Panel
      title="Your shop links"
      subtitle="Share these. A fan who buys through one earns you both."
    >
      {links.isError && <ErrorNote error={links.error} />}
      {links.isPending && <p className="empty">Loading…</p>}

      {!links.isPending && rows.length === 0 && (
        <p className="empty">
          No links yet. These appear once shops you can link to are available.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="links">
          {rows.map((link) => (
            <li key={link.id} className="link-row">
              <div className="link-row__main">
                <span className="link-row__merchant">{link.merchantName}</span>
                <span className="link-row__url">/l/{link.code}</span>
              </div>
              <button
                className="button button--quiet"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    `${window.location.origin}/v1/links/${link.code}`,
                  );
                }}
              >
                Copy
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

function FansPanel() {
  const connections = useCreatorConnections(true);
  const active = (connections.data ?? []).filter((c) => c.effectiveTo === null);

  return (
    <Panel title="Your fans" subtitle="People earning through your codes.">
      {connections.isError && <ErrorNote error={connections.error} />}
      {connections.isPending && <p className="empty">Loading…</p>}

      {!connections.isPending && active.length === 0 && (
        <p className="empty">Nobody yet. Share a code to get started.</p>
      )}

      {active.length > 0 && (
        <ul className="connections">
          {active.map((connection) => (
            <li key={connection.id} className="connection">
              <div>
                <span className="connection__name">
                  {connection.memberDisplayName ?? 'A fan'}
                </span>
                <span className="connection__terms">
                  keeps {Math.round(connection.terms.memberBps / 100)}% · you get{' '}
                  {Math.round(connection.terms.creatorBps / 100)}%
                </span>
              </div>
              <span className="connection__since">
                since{' '}
                {new Date(connection.effectiveFrom).toLocaleDateString(undefined, {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function CreatorActivity() {
  const activity = useCreatorActivity(true);
  if (activity.isError) return <ErrorNote error={activity.error} />;
  return <ActivityTable rows={activity.data?.data} loading={activity.isPending} />;
}
